import { FC, useMemo } from "react";
import {
  ICPWinLossIndustryDistribution,
  ICPIndustryDistribution,
} from "../../../../apis/companySearch/icpDistribution";
import { useTaxonomyContext } from "../../../../contexts/Taxonomy/TaxonomyContext";
import { hasWinLossData } from "../../utils";
import { GenericICPWinLossChart } from "./GenericICPWinLossChart";

interface ICPWinLossChartProps {
  /** Win/loss distribution data from API */
  data: ICPWinLossIndustryDistribution[];
  /** Height of the chart container */
  height?: string;
  /** Optional total count to display (overrides calculated total from data) */
  totalCount?: number;
  /** Whether the chart is loading */
  isLoading?: boolean;
  /** Distribution data to determine ordering (optional) */
  distributionData?: ICPIndustryDistribution[];
}

/**
 * Component that displays win/loss industry distribution chart with stacked columns
 * Each column shows win and loss segments that total 100%
 */
export const ICPWinLossChart: FC<ICPWinLossChartProps> = ({
  data,
  height = "350px",
  totalCount,
  isLoading = false,
  distributionData,
}) => {
  const { hgIndustries } = useTaxonomyContext();

  const industriesMap = useMemo(() => {
    const map = {} as Record<number, string>;
    for (const industry of hgIndustries || []) {
      if (typeof industry.id === "number") {
        map[industry.id] = industry.name || "";
      }
    }
    return map;
  }, [hgIndustries]);

  const getCategoryName = useMemo(
    () => (item: ICPWinLossIndustryDistribution) =>
      industriesMap[item.industryId] || `Industry ${item.industryId}`,
    [industriesMap]
  );

  // Sort to match distribution chart ordering
  // If distribution data is provided, use its ordering; otherwise fall back to total count
  const orderMap = useMemo(() => {
    if (distributionData && distributionData.length > 0) {
      // Create ordering based on distribution data (sorted by share descending)
      const distributionOrder = [...distributionData]
        .sort((x, y) => y.share - x.share)
        .map((item, index) => ({
          industryId: item.industryId,
          order: index,
        }));

      return new Map(
        distributionOrder.map((item) => [item.industryId, item.order])
      );
    }
    return new Map();
  }, [distributionData]);

  const sortData = useMemo(
    () =>
      (
        a: ICPWinLossIndustryDistribution,
        b: ICPWinLossIndustryDistribution
      ) => {
        if (orderMap.size > 0) {
          const orderA = orderMap.get(a.industryId) ?? 999;
          const orderB = orderMap.get(b.industryId) ?? 999;
          return orderA - orderB; // Sort by distribution order (ascending order index)
        }

        // Fallback to total count descending if no distribution data
        return b.totalCount - a.totalCount;
      },
    [orderMap]
  );

  // Show skeleton when loading, or don't render if there's no meaningful win/loss data when not loading
  if (!isLoading && !hasWinLossData(data)) {
    return null;
  }

  return (
    <GenericICPWinLossChart
      data={data}
      getCategoryName={getCategoryName}
      height={height}
      totalCount={totalCount}
      sortData={sortData}
      segment="Industry"
      isLoading={isLoading}
    />
  );
};
